import moment from 'moment'
import { get, has } from 'lodash'

// The limit actually in force for a resource — the plan's included amount with
// any admin override folded in, PLUS purchased add-ons (025-subscription-addons).
//
// `field` is a plan field name: 'kits' | 'customIcons' | 'monthlyPageviews' |
// 'monthlyBandwidth'. `-1` means unlimited.
//
// Reach for this whenever the question is "how much does this customer actually
// have" — usage counters, progress bars, overage thresholds. Read
// `subscription.plan.*` instead when the question is "what does this tier
// include": the account plan card and the plan comparison surfaces deliberately
// show the base so customers can still see what their plan comes with.
//
// Composed server-side by `Subscriptions.EffectiveLimits` (one seam, shared with
// the entitlement checks and the metered-overage reporter) and serialized as
// `effective_limits`. Falls back to the plan field so a subscription payload
// serialized before this attribute existed still renders a sane number.
export function effectiveLimit(subscription, field) {
  return get(subscription, ['effectiveLimits', field], null) ?? get(subscription, ['plan', field], 0)
}

// The sentinel every limit in the system uses for "no ceiling" — plan fields,
// admin overrides, and the metered-billing caps alike. Mirrors
// `Fontawesome.UsageLimits.unlimited/0` server-side.
export const UNLIMITED = -1

// How far a customer is through a limit, as a percentage — their *overage
// allowance* (the band between their pre-paid limit and the metered-billing cap
// they set), or the pre-paid limit itself.
//
// Neither is always a positive number, so the division needs guarding:
// an unlimited cap makes it `UNLIMITED`, metered billing being off makes it `0`,
// and a cap that sits below the pre-paid limit (possible once add-ons raise that
// limit) makes it negative. Dividing naively yields a negative percentage,
// `Infinity`, or `NaN` — none of which compare usefully against the 60/90/100
// thresholds the usage warnings key off, and a negative silently suppresses
// every one of them.
export function overagePercentage(usage, limit) {
  // An unbounded allowance can never be a fraction of the way used up.
  if (limit === UNLIMITED) return 0

  // No allowance at all (or one the pre-paid limit already exceeds): any
  // overage at all has used up everything there was to use.
  if (!(limit > 0)) return usage > 0 ? 100 : 0

  return (usage / limit) * 100
}

export function determinations({ subscription, isBacker, preOrderBackerStage, permits }) {
  const exists = subscription !== null && typeof subscription !== 'undefined'
  const periodEndAt = get(subscription, ['periodEndAt'])
  const status = get(subscription, ['status'])
  const lastPaymentFailedAt = get(subscription, ['lastPaymentFailedAt'])
  const paymentUpdatedAt = get(subscription, ['paymentUpdatedAt'])
  const isBraintree = get(subscription, ['type'], false) === 'braintree'
  const isComped = get(subscription, ['type'], false) === 'comped'
  const isEmployee = get(subscription, ['type'], false) === 'employee'
  const isFree = get(subscription, ['type'], false) === 'free'
  const isPaypal = get(subscription, ['type'], false) === 'paypal'
  const isPro = !isFree
  const isStripe = get(subscription, ['type'], false) === 'stripe'
  const isAnnual = get(subscription, ['plan', 'annual'], true)
  const periodWord = isAnnual ? 'year' : 'month'
  const periodSuffix = isAnnual ? 'yr' : 'mo'
  const isSeatUpgradeable = isStripe || isBraintree
  const isFa6Upgradeable = (isStripe || isBraintree) && get(subscription, ['discounts'], []).length === 0
  const active = get(subscription, ['status']) === 'active'
  const ended = !!get(subscription, ['endedAt'])
  const cancelAtPeriodEnd = get(subscription, ['cancelAtPeriodEnd'], false)
  const withinRenewalGracePeriod = get(subscription, ['withinRenewalGracePeriod'], false)
  const withinEndingGracePeriod = get(subscription, ['withinEndingGracePeriod'], false)
  const renewingSoon = active && !cancelAtPeriodEnd && withinRenewalGracePeriod
  const pastDue = status === 'past_due' || (periodEndAt && moment().isAfter(moment(periodEndAt)))
  const paymentFailed = !!lastPaymentFailedAt
  const paymentUpdated = !!paymentUpdatedAt
  const updatedPaymentAfterFailure = !lastPaymentFailedAt || !paymentUpdatedAt ? false : moment(lastPaymentFailedAt).isBefore(moment(paymentUpdatedAt))
  const paymentOnFile = get(subscription, ['paymentOnFile'], false)
  const updatedPeriod = moment(lastPaymentFailedAt).isBefore(moment(periodEndAt))
  const hasActivePaidSubscription = exists && active && !isFree
  const hasActiveProOnlySubscription = hasActivePaidSubscription && has(permits, ['yep', 'accessToPro']) && !has(permits, ['yep', 'accessToProPlus'])
  const hasActiveProOnlySubscriptionWithDownload =
    hasActivePaidSubscription && has(permits, ['yep', 'accessToProDownload']) && !has(permits, ['yep', 'accessToProPlusDownload'])
  const hasActiveProPlusSubscription = hasActivePaidSubscription && has(permits, ['yep', 'accessToProPlus'])
  const hasActiveProPlusSubscriptionWithDownload = hasActivePaidSubscription && has(permits, ['yep', 'accessToProPlusDownload'])

  let indication = 'normal'

  if (!subscription && !isBacker) {
    indication = 'useless'
  }

  if (renewingSoon) {
    indication = 'info'
  }

  if (renewingSoon && !paymentOnFile) {
    indication = 'warning'
  }

  if (withinRenewalGracePeriod && cancelAtPeriodEnd) {
    indication = 'warning'
  }

  if (!cancelAtPeriodEnd && paymentFailed && !updatedPaymentAfterFailure) {
    indication = 'danger'
  }

  return {
    exists,
    hasActivePaidSubscription,
    hasActiveProOnlySubscription,
    hasActiveProOnlySubscriptionWithDownload,
    hasActiveProPlusSubscription,
    hasActiveProPlusSubscriptionWithDownload,
    indication,
    isBacker,
    preOrderBackerStage: isBacker && !exists ? preOrderBackerStage : undefined,
    isBraintree,
    isComped,
    isEmployee,
    isFree,
    isPaypal,
    isPro,
    isStripe,
    isAnnual,
    periodWord,
    periodSuffix,
    isSeatUpgradeable,
    isFa6Upgradeable,
    active,
    ended,
    cancelAtPeriodEnd,
    withinRenewalGracePeriod,
    withinEndingGracePeriod,
    renewingSoon,
    pastDue,
    paymentFailed,
    paymentUpdated,
    paymentOnFile,
    updatedPaymentAfterFailure,
    updatedPeriod
  }
}
